'use client';

import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import ControlledInput from 'src/components/ControlledFields/ControlledInput';
import { Dialog } from 'src/components/PulseUI/Dialog';
import { PulseButton } from 'src/components/PulseUI/PulseButton';
import { HubSpotCompanyField } from 'src/components/Settings/Companies/components/HubSpotCompanyField';
import { COMPANY_NAME_MAX_LENGTH_RULE } from 'src/components/Settings/Companies/components/HubSpotCompanyField/constants';
import { getCompanySaveError } from 'src/components/Settings/Companies/components/HubSpotCompanyField/utils';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import { useDashboard } from 'src/context/providers/DashboardProvider';
import {
  useGetAllCompaniesQuery,
  useCreateDemoEnvMutation,
} from 'src/api/redux';

interface AddDemoEnvironmentProps {
  open: boolean;
  onClose: () => void;
}

interface DemoCompanyFormValues {
  name: string;
  hubspot_company_id: string | null;
  hubspot_company_name: string | null;
  hubspot_domain: string | null;
}

const DEFAULT_VALUES: DemoCompanyFormValues = {
  name: '',
  hubspot_company_id: null,
  hubspot_company_name: null,
  hubspot_domain: null,
};

/**
 * @description     AddDemoEnvironment component is used to display the add company dialog in the admin dashboard
 * @param param0    open, onClose
 * @returns         AddDemoEnvironment component
 */
const AddDemoEnvironment = ({ ...props }: AddDemoEnvironmentProps) => {
  const log = useLogging();
  const { setNotification } = useNotification();
  const { showAddDemoEnvModal } = useDashboard();

  const { refetch: refetchAllCompanies } = useGetAllCompaniesQuery({});
  const [createDemoEnv] = useCreateDemoEnvMutation();

  const [isLoading, setIsLoading] = useState(false);

  const methods = useForm<DemoCompanyFormValues>({
    defaultValues: DEFAULT_VALUES,
  });
  const {
    handleSubmit,
    reset,
    setError,
    formState: { isValid },
  } = methods;

  // The dialog stays mounted between opens, so every close clears the form
  // for the next one.
  const handleClose = () => {
    reset(DEFAULT_VALUES);
    props.onClose();
  };

  const onSubmit = async ({
    name,
    ...hubspotLinkDetails
  }: DemoCompanyFormValues) => {
    const trimmedName = name?.trim() ?? '';
    setIsLoading(true);
    try {
      // Sales creates the demo partway through a deal, so its HubSpot record
      // usually already exists and can be linked now (MKT-669).
      await createDemoEnv({
        name: trimmedName,
        ...hubspotLinkDetails,
      }).unwrap();

      log.event('Demo Company created', {
        source: 'AddDemoEnvironment',
        data: {
          name: trimmedName,
          hubspot_company_id: hubspotLinkDetails.hubspot_company_id,
        },
      });
      setNotification({
        title: 'Success',
        message: 'Demo Company created',
        type: 'success',
      });
      handleClose();
    } catch (err: unknown) {
      const { message, isHubSpotLinkError } = getCompanySaveError(
        err,
        'Error creating demo company'
      );
      // The dialog stays open either way so no input is lost.
      if (isHubSpotLinkError) {
        setError('hubspot_company_id', { message });
      } else {
        log.exception('Error creating demo company', err);
        setNotification({
          title: 'Error',
          message,
          type: 'error',
        });
      }
    } finally {
      refetchAllCompanies();
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      isOpen={showAddDemoEnvModal}
      onClose={handleClose}
      size="lg"
      // Sized to its content, like the company dialog: the fixed size height
      // cuts off the HubSpot picker and linked-company card.
      className="!h-auto"
    >
      <Dialog.Header>
        <h2 className="text-xl font-semibold">Add Demo Company</h2>
      </Dialog.Header>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} data-testid="demo-company-form">
          <Dialog.Content className="p-6 flex flex-col gap-4">
            {/* First, so the name can come from HubSpot rather than be typed. */}
            <HubSpotCompanyField isOpen={showAddDemoEnvModal} isEdit={false} />
            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold text-neutral-700">
                Name
              </label>
              <ControlledInput
                name="name"
                placeholder="Company name"
                rules={{
                  validate: (value: string) =>
                    !!value?.trim() || 'This field is required',
                  maxLength: COMPANY_NAME_MAX_LENGTH_RULE,
                }}
                fullWidth
                useLabel={false}
              />
            </div>
          </Dialog.Content>

          <Dialog.Actions>
            <PulseButton
              variant="outline"
              color="neutral"
              onClick={handleClose}
              aria-label="Cancel"
              data-testid="cancel-button"
            >
              Cancel
            </PulseButton>
            <PulseButton
              type="submit"
              variant="solid"
              color="primary"
              loading={isLoading}
              disabled={!isValid}
              aria-label="Create Demo Company"
              data-testid="create-demo-company-button"
            >
              Create Demo Company
            </PulseButton>
          </Dialog.Actions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default AddDemoEnvironment;
